import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Linking, Image } from 'react-native';
import i18n from 'i18n-js';
import { connect, useSelector } from 'react-redux';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import DocumentPicker from 'react-native-document-picker';

import {
  isPhone,
  isPhoneNative,
  Alert,
  isIOS,
  toBase64,
  isWeb,
  localTime,
  Popup,
  getLinkingModalConfig,
  LEVELS,
  FILE_EXTENSIONS,
  navigationRef,
} from '../../helpers';
import { fullWidth, scale as initialScale, notchBottom, scaleOP, width } from '@components/frame';
import LineButton from '@components/common/lineButton';
import {
  limitMinimumSize,
  formatDate,
  TAG_KEYS,
  KEYS_TAGS,
  favoriteKey,
  smallImageHeight,
  SCOPE_ID_TO_TAG_NAME,
  OP_TAG_NAMING,
} from '../imageHandling/common';
import CropImageMobile from '@v2/components/imageHandling/components/Cropper/components/CropperMobile';
import CropImageTablet from '@v2/components/imageHandling/components/Cropper/components/CropperTablet';
import CropImageWeb from '@v2/components/imageHandling/components/Cropper/components/CropperWeb';

import ColorsWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import Button from '@styles/buttons';
import { getBoxShadowStyle } from '@styles/shadow';

import { fontOP, font as initialFont } from '@styles/fonts';
import { selectNcFileUploadDocumentPickerOpened, setNcFileUploadDocumentPickerOpened } from '@reducers/details';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import Search from '@components/search';
import CloseButton from '@components/closeButton';
import { setTotalSearchResult } from '@reducers/search';
import {
  IMAGE_UPLOAD_BUTTON_TEXT,
  IMAGE_UPLOAD_FOOTER_TEXT,
  IMAGE_UPLOAD_MAIN_TEXT,
  MODAL_ANIMATION_TIMING,
  SCREEN_BORDER_RADIUS,
  TABLE_NAMES,
  TEST_IDS,
} from '../v2/pages/common/constants/base.constants';
import { addProductionInformationV2 } from '@reducers/manufacture';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { updateImageMetadata, uploadImageDocument } from '@reducers/v2/imageHandlingV2/thunks';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { addOrUpdateImageDocument, updateImageDocumentInDict } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { OP_DOCUMENTS_DISPLAY_MODE } from '@components/operatorPanel/constants/operatorPanel.constants';
import AttachEntitiesList from '@components/common/AttachEntitiesList';
import ModalWrapper from '@components/modal/ModalWrapper';
import { PADDING, IMAGE_TYPES, UNSUPPORTED_TYPES, isBlockedNcFileExtension } from './constants';

const ImageUploadComponent = ({
  C,
  hide,
  ncFile,
  tags,
  documentType,
  uploadEnd,
  uploadStart,
  saveToMainStore,
  saveToEnd,
  scrollRef,
  onlySelectImage,
  mainText,
  headerText,
  onSuccessAddingExisting,
  footerText,
  withoutAddExisting,
  addExistingText,
  headerDescription,
  addingNcFile,
  addExistingSearchLevel,
  noPhoto,
  shouldBrowse,
  setNcFileUploadDocumentPickerOpened,
  onSuccessImageUpload,
  setTotalSearchResult,
  addProductionInformationV2,
  imageDocumentReferences = [],
  scale: scaleProp = initialScale,
  font: fontProp = initialFont,
  expectedFileName,
}) => {
  const [hoverFile, setHoverFile] = useState(false);
  const [isMultiFileError, setMultiFileError] = useState(false);
  const [isTypeFileError, setTypeFileError] = useState(false);
  const [isCropMode, setIsCropMode] = useState(false);
  const [image, setImage] = useState(null);
  const [imageLinks, setImageLinks] = useState(tags);
  const [isImageUploadModalVisible, setIsImageUploadModalVisible] = useState(true);

  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();
  const scale = safeIsOperatorPanelInfoScreen ? scaleOP : scaleProp;
  const font = safeIsOperatorPanelInfoScreen ? fontOP : fontProp;

  const roundTop = !ncFile;

  const dispatch = useAppDispatch();
  const ncFileUploadDocumentPickerOpened = useSelector(selectNcFileUploadDocumentPickerOpened);

  const onImageUploadModalClose = () => {
    setIsImageUploadModalVisible(false);
    hide();
  };

  useEffect(() => {
    const paste = (pasteEvent) => {
      const item = pasteEvent.clipboardData.items[0];
      if (item.type.indexOf('image') === 0) {
        const blob = item.getAsFile();
        uploadImage(blob);
      }
    };

    if (isWeb) {
      window.document.addEventListener('paste', paste);
    }

    return () => {
      if (isWeb) {
        window.document.removeEventListener('paste', paste);
      }
    };
  }, []);

  useEffect(() => {
    if (shouldBrowse && !isWeb && !ncFileUploadDocumentPickerOpened) {
      pickFile();
    }
  }, [shouldBrowse, isWeb, ncFileUploadDocumentPickerOpened]);

  const cropImage = async (photo) => {
    setImage(photo);
    setIsCropMode(true);
  };

  const uploadImage = async (data) => {
    if (data?.errorCode) {
      Alert({
        title: 'No access to camera.',
        text: 'Enable your camera to continue.',
        actions: [
          {
            text: 'Cancel',
            onPress: () => console.log('Cancel Pressed'),
            style: 'primary',
          },
          {
            text: 'Open settings',
            onPress: () => {
              if (isIOS) {
                Linking.openURL('app-settings:');
              } else {
                Linking.openSettings();
              }
            },
          },
        ],
      });
      return;
    }

    if (isWeb) {
      data.uri = await toBase64(data);
    }

    const img = data?.assets?.[0] || data;

    if (img?.uri) {
      let sizeInMB;
      if (isWeb) {
        sizeInMB = Number((img.size / (1000 * 1000)).toFixed(1));
      } else {
        if (img.fileSize) {
          sizeInMB = Number((img.fileSize / (1024 * 1024)).toFixed(1));
        } else {
          sizeInMB = Number((img.size / (1000 * 1000)).toFixed(1));
        }
      }

      if (sizeInMB >= 15) {
        Alert({
          title: i18n.t('The upload failed because your file was too big'),
          text: `${i18n.t('The maximum size is 15MB. Your file size')}:${sizeInMB}`,
        });
        return;
      }

      // Validate filename for NC file version uploads
      const selectedFileName = img.fileName || img.name;

      // For NC files, block obviously wrong file types
      if (addingNcFile && selectedFileName) {
        const selectedExt = selectedFileName.split('.').pop()?.toLowerCase() || '';
        if (isBlockedNcFileExtension(selectedExt)) {
          Alert({
            title: i18n.t('Invalid file type'),
            text: i18n.t('This file type cannot be used as an NC file.'),
            actions: [{ text: i18n.t('OK'), style: 'primary' }],
          });
          return;
        }
      }

      if (expectedFileName && selectedFileName) {
        const expectedHasExtension = expectedFileName.includes('.');
        const selectedHasExtension = selectedFileName.includes('.');

        if (!expectedHasExtension && selectedHasExtension) {
          // NC file has no extension — uploaded file must also have no extension
          const selectedExt = selectedFileName.split('.').pop()?.toLowerCase() || '';
          Alert({
            title: i18n.t('Invalid extension'),
            text: `${i18n.t('To add a new version, the NC file extension must match.')}\n\n${i18n.t(
              'Extension of file you added'
            )}:\n.${selectedExt}\n\n${i18n.t('Extension of selected NC File')}:\n${i18n.t('none')}`,
            actions: [{ text: i18n.t('OK'), style: 'primary' }],
          });
          return;
        }

        if (expectedHasExtension) {
          const selectedExt = selectedFileName.split('.').pop()?.toLowerCase() || '';
          const expectedExt = expectedFileName.split('.').pop()?.toLowerCase() || '';

          // Check extension first
          if (selectedExt !== expectedExt) {
            Alert({
              title: i18n.t('Invalid extension'),
              text: `${i18n.t('To add a new version, the NC file extension must match.')}\n\n${i18n.t(
                'Extension of file you added'
              )}:\n.${selectedExt}\n\n${i18n.t('Extension of selected NC File')}:\n.${expectedExt}`,
              actions: [{ text: i18n.t('OK'), style: 'primary' }],
            });
            return;
          }
        }

        // Check full name match
        if (selectedFileName !== expectedFileName) {
          Alert({
            title: i18n.t('NC File Naming Mismatch'),
            text: `${i18n.t('To add a new version, the NC file name must match.')}\n\n${i18n.t('File you added')}:\n${selectedFileName}\n\n${i18n.t(
              'Selected NC File'
            )}:\n${expectedFileName}`,
            actions: [{ text: i18n.t('OK'), style: 'primary' }],
          });
          return;
        }
      }

      const photoOriginal = isWeb
        ? img
        : {
            uri: img.uri,
            type: img.type,
            name: img.fileName || img.name,
          };
      const isImage = IMAGE_TYPES.includes(photoOriginal.type);

      if (isImage) {
        cropImage(photoOriginal);
      } else {
        finishUploading(photoOriginal);
      }
    } else {
      Alert({
        title: i18n.t('Something went wrong'),
      });
      return;
    }
  };

  const finishUploading = async (data, cropDetails, copedImage) => {
    if (uploadStart) {
      uploadStart(data);
    }

    const tempID = `TEMPIMAGEID-${Math.random().toString(36).substring(7)}`;

    const isPDF = data.type.includes('pdf');
    const isDocument = isPDF || !IMAGE_TYPES.includes(data.type);

    if (onlySelectImage) {
      onImageUploadModalClose();
      return;
    }

    let imageCropSettings = null;

    const getImageSize = new Promise(
      (resolve, reject) => {
        Image.getSize(data.uri, (width, height) => {
          resolve({ width, height });
        });
      },
      (error) => reject(error)
    );

    const imageInfo = isDocument ? {} : await getImageSize;

    if (cropDetails) {
      const { x, y, height, width, angle } = cropDetails;
      const toTwo = (n) => Number(Number(n).toFixed(2));

      if (angle == 0 || angle == 180 || angle == 360) {
        imageCropSettings = {
          upperLeftXPosition: toTwo(x / imageInfo.width),
          lowerRightXPosition: toTwo((x + width) / imageInfo.width),
          upperLeftYPosition: toTwo(y / imageInfo.height),
          lowerRightYPosition: toTwo((y + height) / imageInfo.height),
        };
      } else {
        imageCropSettings = {
          upperLeftXPosition: toTwo(x / imageInfo.height),
          lowerRightXPosition: toTwo((x + width) / imageInfo.height),
          upperLeftYPosition: toTwo(y / imageInfo.width),
          lowerRightYPosition: toTwo((y + height) / imageInfo.width),
        };
      }
      imageCropSettings.pageNumber = 1;
      imageCropSettings.rotationAngle = angle;
    }

    const imageContexts = imageLinks?.map((t) => ({ scopeId: t?.scopeId, scopeReferenceId: t?.scopeReferenceId })) ?? [];

    dispatch(uploadImageDocument({ photo: data, tags: imageLinks, documentType, imageCropSettings, tempID, isPDF, isDocument, imageContexts }));

    if (isPDF || isDocument) {
      data.uri = null;
    }

    if (saveToMainStore) {
      const activeImageWidth = limitMinimumSize((scale(smallImageHeight) * data.width) / data.height);

      const uri = cropDetails?.base64 || data.uri;

      const extension = data?.name ? data.name?.slice(data.name.lastIndexOf('.') + 1) : null;

      const newImage = {
        id: tempID,
        documentId: tempID,
        base64: uri,
        url: uri,
        urlSmall: uri,
        originUrl: uri,
        width: data.width,
        height: data.height,
        widthOriginal: data.width,
        heightOriginal: data.height,
        isPreview: isPDF || isDocument,
        isProcessing: true,
        showPDFUploading: isPDF,
        showDocumentUploading: isDocument,
        isDocument,
        isPDF,
        pageCount: 1,
        pageNumber: 1,
        fileName: data.name,
        ...(extension ? { extensionOriginal: extension } : {}),
        local: true,
        customRotate: { 1: 0 },
        activeImageWidth,
        updatedDate: formatDate(localTime().format('YYYY-MM-DD HH:mm')),
        startedEditingImage: true,
        tags: imageLinks?.map((el) => ({
          ...el,
          key: TAG_KEYS[el.scopeId],
          value: el.scopeReferenceId,
          name: el.scopeReferenceName,
          id: Math.random().toString(36).substring(7),
          selected: el.selected !== undefined ? el.selected : true,
          canBeFavorite: favoriteKey.includes(TAG_KEYS[el.scopeId]),
          favorite: el.isFavorite,
        })),
        documentType,
        documentTag: documentType,
      };

      imageContexts.forEach((imageContext) => {
        dispatch(addOrUpdateImageDocument({ imageContext, document: newImage, saveToEnd }));
      });

      setTimeout(() => {
        if (scrollRef && scrollRef.current) {
          if (saveToEnd) {
            scrollRef.current.scrollToEnd();
          } else {
            scrollRef.current.scrollToIndex({ animated: false, index: 0 });
          }
        }
      }, 1000);
    }

    if (uploadEnd) {
      uploadEnd(copedImage || data);
    }

    onSuccessImageUpload?.();
    onImageUploadModalClose();
  };

  const searchPopupWidth = (isWeb ? fullWidth : width) - scale((isWeb ? fullWidth : width) > 1116 ? PADDING * 2 : 0);
  const targetItemConfigForAddingExistingImageDocument = imageLinks?.[0];

  const onSelectItemDuringAddExisting = async (image) => {
    if (!imageLinks || imageLinks.length === 0) return;

    if (addExistingSearchLevel === LEVELS.NCFiles) {
      await Promise.allSettled(
        imageLinks?.map((t) =>
          addProductionInformationV2({
            ncFileId: image.ncFileId,
            productionOperationId: t?.scopeReferenceId,
          })
        )
      );
      onSuccessAddingExisting?.(image);
    } else {
      const newLinks = imageLinks?.map((t) => ({
        scopeId: t?.scopeId,
        scopeReferenceId: t?.scopeReferenceId,
        // scopeReferenceName,
        isFavorite: t?.isFavorite,
        isActivated: true,
        isFavoriteChanged: false,
      }));

      //Update state locally to immediate feedback for user
      dispatch(updateImageDocumentInDict({ documentId: image.documentId, updatedImageDocument: { ...image, links: newLinks } }));

      newLinks.forEach((link) => {
        dispatch(
          addOrUpdateImageDocument({
            document: { ...image, ...(documentType ? { documentTag: documentType } : {}) },
            imageContext: { scopeId: link.scopeId, scopeReferenceId: link.scopeReferenceId },
          })
        );
      });

      newLinks.forEach((link) => {
        dispatch(
          addOrUpdateImageDocument({
            document: { ...image, ...(documentType ? { documentTag: documentType } : {}) },
            imageContext: { scopeId: link.scopeId, scopeReferenceId: link.scopeReferenceId },
          })
        );
      });

      // Send API request
      const attachmentData = {
        documentId: image.documentId,
        documentLinks: newLinks,
        documentTags: documentType ? [documentType] : [],
      };

      await dispatch(updateImageMetadata({ data: attachmentData, onSuccess: () => onSuccessAddingExisting?.(image) }));
      trackEvent(TRACKING_EVENTS.DOCUMENT, {
        action: 'linked',
        documentType: documentType || '',
        result: 'success',
      });

      const displayMode = navigationRef.current?.getCurrentRoute()?.params?.mode;
      const shouldChangeDisplayMode = displayMode === OP_DOCUMENTS_DISPLAY_MODE.TILE;

      navigationRef.current?.setParams({
        selectedImageKey: image?.contentHash,
        ...(shouldChangeDisplayMode ? { mode: OP_DOCUMENTS_DISPLAY_MODE.GALLERY } : {}),
      });
    }
  };

  // Converts numeric scopeId to the string key that OP_TAG_NAMING expects
  const getOPScopeName = (scopeId) => {
    const stringKey = TAG_KEYS[scopeId];
    return OP_TAG_NAMING?.[stringKey] || SCOPE_ID_TO_TAG_NAME?.[scopeId] || '';
  };

  // Create a component that connects to Redux for the search popup header
  const SearchPopupHeader = connect(({ search: { totalSearchResult: reduxTotalSearchResult } }) => ({
    totalSearchResult: reduxTotalSearchResult,
    scopeReferenceName: targetItemConfigForAddingExistingImageDocument?.scopeReferenceName || '',
  }))(({ totalSearchResult: currentTotalSearchResult, hide: hidePopup, scopeReferenceName }) => (
    <View style={{ paddingHorizontal: scale(PADDING), paddingTop: scale(17), gap: scale(4) }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Text style={[font['Subhead 2'], { color: C('Label/Light/Quaternary') }]}>
          {currentTotalSearchResult ?? 0} {i18n.t('FOUND')}
        </Text>
        {isPhone && (
          <View
            style={[
              {
                position: 'absolute',
                top: scale(-7),
                right: scale(-3),
              },
            ]}
          >
            <CloseButton onPress={hidePopup} C={C} />
          </View>
        )}
      </View>
      {targetItemConfigForAddingExistingImageDocument && (
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%', marginBottom: scale(13) }}>
          <Text style={[font['Title 2'], { color: C('Label/Light/Primary') }]}>
            {addExistingSearchLevel === LEVELS.NCFiles
              ? i18n.t('Add Existing NC File to {{scopeName}} {{scopeReferenceName}}', {
                  scopeName: getOPScopeName(targetItemConfigForAddingExistingImageDocument?.scopeId),
                  scopeReferenceName,
                })
              : i18n.t('Add Existing Image or Document to {{scopeName}} {{scopeReferenceName}}', {
                  scopeName: SCOPE_ID_TO_TAG_NAME?.[targetItemConfigForAddingExistingImageDocument?.scopeId],
                  scopeReferenceName,
                })}
          </Text>
          {!isPhone && (
            <View
              style={[
                {
                  flexShrink: 0,
                  paddingLeft: scale(5),
                  transform: [{ translateY: scale(-7) }],
                },
              ]}
            >
              <CloseButton onPress={hidePopup} C={C} />
            </View>
          )}
        </View>
      )}
    </View>
  ));

  const addExisting = () => {
    // Reset the total search result before opening the popup to avoid showing stale data
    setTotalSearchResult(null);

    Popup({
      Component: (p) => (
        <View style={{ backgroundColor: C('Background/Light/System'), flex: 1 }}>
          <SearchPopupHeader hide={p.hide} />
          <Search
            {...p}
            isPopupMode
            popupWidth={searchPopupWidth}
            tableNamePrefix={TABLE_NAMES.imageUpload}
            route={{
              params: {
                ignoreModeHistory: true,
                level: addExistingSearchLevel ?? LEVELS.ImagesDocuments,
              },
            }}
            onItemSelect={(params) => {
              onSelectItemDuringAddExisting(params);
              p.hide();
            }}
            hideLevelNavigation
            hideScrollHeader
            setTotalSearchResult={setTotalSearchResult}
            searchInputWithModeSelectorStyle={{ marginTop: 0 }}
            scale={scale}
            font={font}
          />
        </View>
      ),
      ...getLinkingModalConfig(scale),
      offsetTop: scale(PADDING),
      offsetBottom: scale(PADDING),
      width: searchPopupWidth,
    });
  };

  const openWebUploader = () => {
    var input = window.document.createElement('input');
    input.type = 'file';

    if (addingNcFile) {
      // NC files can have many extensions (.nc, .tap, .cnc, .h, .i, .mpf, .spf, .min, .eia, .fnc, .gcode, etc.)
      // The `accept` attribute only supports whitelisting, not blacklisting, so we must allow all files
      // and rely on post-selection validation (isBlockedNcFileExtension) to reject invalid file types.
      // The backend already handles it, anyways we do it here as post-validation as well.
      input.accept = '*';
    } else {
      const extensions = Object.values(FILE_EXTENSIONS)
        .filter((ext) => ext !== FILE_EXTENSIONS.UNKNOWN_FILE_EXTENSION)
        .map((ext) => `.${ext}`)
        .join(',');

      input.accept = `image/*,${extensions}`;
    }

    input.onchange = async (e) => {
      var file = e.target.files[0];
      uploadImage(file);
    };

    input.click();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHoverFile(false);
    if (e.dataTransfer.files.length !== 1) {
      setMultiFileError(true);
      return;
    }

    const file = e.dataTransfer.files[0];

    if (UNSUPPORTED_TYPES.includes(file.type)) {
      setTypeFileError(true);
      return;
    }

    // For NC files, block obviously wrong file types (images, archives, executables, etc.)
    if (addingNcFile) {
      const fileName = file.name || '';
      const extension = fileName.split('.').pop()?.toLowerCase();
      if (isBlockedNcFileExtension(extension)) {
        Alert({
          title: i18n.t('Invalid file type'),
          text: i18n.t('This file type cannot be used as an NC file.'),
          actions: [{ text: i18n.t('OK'), style: 'primary' }],
        });
        return;
      }
    }

    setMultiFileError(false);
    setTypeFileError(false);

    uploadImage(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setHoverFile(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setHoverFile(false);
  };

  if (isCropMode) {
    const data = {
      cropped: false,
      width: 2000,
      height: 2000,
      angle: 0,
      base64: image.uri,
      base64Original: image.uri,
      customRotate: {
        1: 0,
      },
      page: 1,
    };
    if (isWeb) {
      return (
        <CropImageWeb
          {...{
            isNewImage: true,
            onBack: () => {
              onImageUploadModalClose();
            },
            onSave: async (cropDetails) => {
              const toFile = (url, filename, mimeType) => {
                return fetch(url, { ignoreInterceptor: 'true' })
                  .then(function (res) {
                    return res.arrayBuffer();
                  })
                  .then(function (buf) {
                    return new File([buf], filename, { type: mimeType });
                  });
              };

              toFile(cropDetails.base64, image.fileName || image.name, image.type).then(function (file) {
                file.uri = cropDetails.base64;
                finishUploading(image, cropDetails, file);
              });
            },
            image: data,
            C,
          }}
        />
      );
    }

    if (isPhone) {
      return (
        <CropImageMobile
          {...{
            isNewImage: true,
            triggerShow: () => {
              onImageUploadModalClose();
            },
            onSave: async (cropDetails) => {
              const copedImage = {
                uri: cropDetails.base64,
                type: image.type,
                name: image.fileName || image.name,
              };
              finishUploading(image, cropDetails, copedImage);
            },
            image: data,
            C,
          }}
        />
      );
    } else {
      return (
        <CropImageTablet
          {...{
            isNewImage: true,
            triggerShow: () => {
              onImageUploadModalClose();
            },
            onSave: async (cropDetails) => {
              const copedImage = {
                uri: cropDetails.base64,
                type: image.type,
                name: image.fileName || image.name,
              };
              finishUploading(image, cropDetails, copedImage);
            },
            image: data,
            C,
          }}
        />
      );
    }
  }

  const onAttachItemPress = ({ link }) => {
    const linkCopy = { ...link };
    delete linkCopy.isSelected;
    setImageLinks((prev) =>
      link?.isSelected
        ? prev?.filter((l) => l.scopeReferenceId !== link.scopeReferenceId)
        : [...prev, { ...linkCopy, isFavorite: link.scopeId === KEYS_TAGS.Machine }]
    );
  };

  const generalImage = !imageLinks?.length;
  const userImage = imageLinks?.find((i) => i.scopeId === KEYS_TAGS.UserAccount);
  const machineImage = !generalImage && !userImage && imageLinks?.[0]?.scopeId === KEYS_TAGS['Machine'];

  const imageText = (() => {
    if (generalImage) {
      return IMAGE_UPLOAD_MAIN_TEXT.ImageOrDocument;
    }
    if (userImage) {
      return IMAGE_UPLOAD_MAIN_TEXT.User;
    }
    if (machineImage) {
      return IMAGE_UPLOAD_MAIN_TEXT.Machine;
    }
    return IMAGE_UPLOAD_MAIN_TEXT.ImageOrDocument;
  })();

  if (isWeb) {
    return (
      <ModalWrapper
        isVisible={isImageUploadModalVisible}
        C={C}
        onBackdropPress={onImageUploadModalClose}
        backdropColor="Overlay/Light/Picture dark"
        backdropOpacity={1}
        animationIn="fadeIn"
        animationOut="fadeOut"
        animationInTiming={MODAL_ANIMATION_TIMING}
        animationOutTiming={MODAL_ANIMATION_TIMING}
      >
        <View
          style={
            isPhoneNative
              ? {
                  width: '100%',
                  bottom: 0,
                  position: 'absolute',
                  paddingBottom: notchBottom + scale(8),
                  paddingHorizontal: scale(8),
                }
              : {
                  alignSelf: 'center',
                  minWidth: scale(600),
                }
          }
        >
          <div onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
            <View
              testID={TEST_IDS.imageUploadPopup.container}
              style={[
                {
                  borderRadius: scale(36),
                  backgroundColor: C('Background/Light/Modal'),
                  width: '100%',
                  height: 'auto',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                  padding: scale(PADDING),
                  paddingTop: scale(23),
                },
              ]}
            >
              <View style={{ position: 'absolute', right: scale(PADDING - 3), top: scale(20) }}>
                <CloseButton onPress={onImageUploadModalClose} C={C} />
              </View>
              <Text style={[font['Title 2'], { color: C('Button/Light/Tertiary Text'), marginBottom: scale(10), paddingHorizontal: scale(PADDING) }]}>
                {i18n.t(headerText || 'Add Image or Document')}
              </Text>
              <Text
                style={[font['Body Large'], { color: C('Button/Light/Tertiary Text'), marginBottom: scale(18), paddingHorizontal: scale(PADDING) }]}
              >
                {i18n.t(headerDescription)}
              </Text>
              {!!imageDocumentReferences.length && (
                <AttachEntitiesList
                  onAttachItemPress={onAttachItemPress}
                  imageDocumentReferences={imageDocumentReferences}
                  imageDocumentLinks={imageLinks}
                  scale={scale}
                  font={font}
                />
              )}
              <View
                style={{
                  width: '100%',
                  borderRadius: scale(18),
                  height: scale(374),
                  backgroundColor: C(hoverFile ? 'Button/Light/Tertiary hover' : 'Background/Light/Field lighter'),
                  borderColor: C('Hairline/Light/Primary'),
                  borderWidth: scale(2),
                  borderStyle: 'dashed',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <View style={{ height: scale(120), justifyContent: 'center' }}>
                  <Svg icon="uploadBig" width={scale(100)} height={scale(85)} color="Overlay/Light/Text Area On Picture" />
                </View>
                <Text style={[font['Title 2'], { color: C('Button/Light/Tertiary Text'), marginTop: scale(10), paddingHorizontal: scale(PADDING) }]}>
                  {i18n.t('Upload')}
                </Text>
                <Text
                  style={[font['Body Large'], { color: C('Button/Light/Tertiary Text'), marginTop: scale(15), paddingHorizontal: scale(PADDING) }]}
                >
                  {i18n.t(mainText || imageText)}
                </Text>
                <View
                  style={{
                    flexDirection: 'row',
                    gap: scale(9),
                    justifyContent: 'center',
                    marginTop: scale(10),
                    alignItems: 'center',
                    height: scale(22),
                  }}
                >
                  <View style={{ height: StyleSheet.hairlineWidth, backgroundColor: C('Hairline/Light/Transpar'), width: scale(152) }} />
                  <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{i18n.t('or')}</Text>
                  <View style={{ height: StyleSheet.hairlineWidth, backgroundColor: C('Hairline/Light/Transpar'), width: scale(152) }} />
                </View>
                <View style={{ marginTop: scale(10) }}>
                  <Button onPress={openWebUploader} variant="Dialog L" type="Secondary" borderRadius={scale(40)} minWidth={scale(360)}>
                    {i18n.t('Browse Files...')}
                  </Button>
                </View>
                {isMultiFileError && (
                  <Text style={[font['Title 2'], { color: C('Label/Light/Error'), position: 'absolute', bottom: scale(34) }]}>
                    {i18n.t('You can only drag one file at the time')}
                  </Text>
                )}
                {isTypeFileError && (
                  <Text style={[font['Title 2'], { color: C('Label/Light/Error'), position: 'absolute', bottom: scale(34) }]}>
                    {i18n.t('Unsupported file type')}
                  </Text>
                )}
              </View>
              {!withoutAddExisting && (
                <>
                  <View
                    style={{
                      width: '100%',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: scale(PADDING),
                      paddingHorizontal: scale(12),
                      gap: scale(9),
                    }}
                  >
                    <View
                      style={{
                        flex: 1,
                        height: StyleSheet.hairlineWidth,
                        backgroundColor: C('Hairline/Light/Transpar'),
                        marginTop: scale(3),
                      }}
                    />
                    <Text style={[font['Body'], { color: C('Button/Light/Tertiary Text'), flexShrink: 0 }]}>{i18n.t('or')}</Text>
                    <View
                      style={{
                        flex: 1,
                        height: StyleSheet.hairlineWidth,
                        backgroundColor: C('Hairline/Light/Transpar'),
                        marginTop: scale(3),
                      }}
                    />
                  </View>
                  <View
                    style={{
                      width: '100%',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      marginTop: scale(PADDING),
                    }}
                  >
                    <Button
                      {...{
                        variant: 'Dialog L',
                        type: 'Primary',
                        minWidth: scale(564),
                        borderRadius: scale(40),
                        onPress: () => {
                          onImageUploadModalClose();
                          addExisting();
                        },
                        scale,
                        font,
                      }}
                    >
                      {i18n.t(addExistingText || IMAGE_UPLOAD_BUTTON_TEXT.short)}
                    </Button>
                  </View>
                  <Text style={[font['Caption 1'], { marginTop: scale(3), color: C('Label/Light/Tertiary') }]}>
                    {i18n.t(footerText || IMAGE_UPLOAD_FOOTER_TEXT.AddImageOrDocument)}
                  </Text>
                </>
              )}
            </View>
          </div>
        </View>
      </ModalWrapper>
    );
  }

  const pickFile = async () => {
    setNcFileUploadDocumentPickerOpened(true);

    const fileTypes = addingNcFile
      ? [DocumentPicker.types.plainText]
      : [
          DocumentPicker.types.images,
          DocumentPicker.types.doc,
          DocumentPicker.types.docx,
          DocumentPicker.types.xls,
          DocumentPicker.types.xlsx,
          DocumentPicker.types.csv,
          DocumentPicker.types.pptx,
          DocumentPicker.types.ppt,
          DocumentPicker.types.xml,
          DocumentPicker.types.pdf,
          'public.message',
          'com.autodesk.dwg',
          'com.autodesk.dxf',
          'public.stereolithography',
          'com.autodesk.dft',
          'com.autodesk.par',
          'com.autodesk.igs',
          'public.x-t',
          'public.step',
          'public.stp',
          DocumentPicker.types.zip,
        ];

    const res = await DocumentPicker.pick({
      type: fileTypes,
    });
    uploadImage(res);
  };

  const actions = [
    ...(!noPhoto && !addingNcFile
      ? [
          {
            name: 'Take Photo',
            action: () => launchCamera({ mediaType: 'photo', quality: 1, base64: true }, uploadImage),
          },
          {
            name: 'Select Photo',
            action: () => launchImageLibrary({ mediaType: 'photo', quality: 1, base64: true }, uploadImage),
          },
        ]
      : []),
    {
      name: IMAGE_UPLOAD_BUTTON_TEXT.short,
      action: () => {
        onImageUploadModalClose();
        addExisting();
      },
    },

    {
      name: 'Browse Files...',
      action: pickFile,
    },
  ];

  if (shouldBrowse && !isWeb) {
    return;
  }

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <ModalWrapper
      isVisible={isImageUploadModalVisible}
      C={C}
      onBackdropPress={onImageUploadModalClose}
      backdropColor="Overlay/Light/Picture dark"
      backdropOpacity={1}
      animationIn="fadeIn"
      animationOut="fadeOut"
      animationInTiming={MODAL_ANIMATION_TIMING}
      animationOutTiming={MODAL_ANIMATION_TIMING}
    >
      <View
        style={
          isPhoneNative
            ? {
                width: '100%',
                bottom: 0,
                position: 'absolute',
                paddingBottom: notchBottom + scale(8),
                paddingHorizontal: scale(8),
              }
            : {
                width: scale(330),
                alignSelf: 'center',
              }
        }
      >
        {/* {ncFile && (
          <View
            style={[
            shadowStyles,
              {
                borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                backgroundColor: C('Background / Light / Field'),
                height: scale(66),
                width: '100%',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
              },
              isPhone
                ? {
                    borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                    borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                    marginBottom: scale(8),
                  }
                : {
                    borderBottomColor: C('Hairline/Light/No trans'),
                    borderBottomWidth: StyleSheet.hairlineWidth,
                  },
            ]}
          >
            <View>
              <Text style={[font.Headline, { color: C('Label/Light/Primary'), textAlign: 'center' }]}>{i18n.t('Add image to NC File')}</Text>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>{ncFile}</Text>
            </View>
          </View>
        )} */}
        <View
          style={[
            shadowStyles,
            {
              marginBottom: scale(isPhone ? 8 : 0),
              backgroundColor: C('Background / Light / Field'),
              borderRadius: scale(isPhone ? SCREEN_BORDER_RADIUS : 0),
            },
            roundTop && {
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
          ]}
        >
          {actions.map((a, index) => (
            <LineButton
              key={a.name}
              {...{
                height: 50,
                onPress: () => {
                  a.action();
                },
                text: a.name,
                color: 'Link/Light/Primary',
                C,
                shadow: false,
                borderBottom: isPhone ? index !== actions.length - 1 : true,
                borderRadiusTop: isPhone || roundTop ? index === 0 : false,
                borderRadiusBottom: isPhone ? index === actions.length - 1 : false,
                font: 'Body Large',
              }}
            />
          ))}
        </View>
        <View
          style={[
            {
              backgroundColor: C('Background / Light / Field'),
              borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
            isPhone && {
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
          ]}
        >
          <LineButton
            {...{
              onPress: onImageUploadModalClose,
              text: 'Cancel',
              color: 'Link/Light/Primary',
              font: 'Headline Large',
              C,
              height: 50,
              borderRadiusTop: isPhone,
            }}
          />
        </View>
      </View>
    </ModalWrapper>
  );
};

const RenderComponent = connect(null, {
  setNcFileUploadDocumentPickerOpened,
  setTotalSearchResult,
  addProductionInformationV2,
})(ColorsWrapper(ImageUploadComponent));

class ImageUpload extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showUploadImage: false,
      ncFile: '',
      tags: [],
      documentType: null,
      uploadStart: null,
      uploadEnd: null,
      saveToMainStore: false,
      saveToEnd: false,
      onlySelectImage: false,
      scrollRef: null,
      mainText: null,
      headerText: null,
      onSuccessAddingExisting: null,
      footerText: null,
      withoutAddExisting: false,
      addExistingText: null,
      headerDescription: null,
      addingNcFile: false,
      addExistingSearchLevel: null,
      noPhoto: false,
      imageDocumentReferences: [],
      scale: undefined,
      font: undefined,
      expectedFileName: null,
    };
  }

  show = ({
    ncFile,
    tags,
    documentType = null,
    uploadEnd,
    uploadStart,
    saveToMainStore = false,
    saveToEnd = false,
    scrollRef,
    onlySelectImage,
    mainText,
    headerText,
    onSuccessAddingExisting,
    footerText,
    withoutAddExisting = false,
    addExistingText,
    headerDescription,
    addingNcFile,
    addExistingSearchLevel,
    noPhoto,
    onSuccessImageUpload,
    imageDocumentReferences = [],
    scale = undefined,
    font = undefined,
    expectedFileName = null,
  }) => {
    this.setState({
      shouldBrowse: false,
      showUploadImage: true,
      ncFile,
      tags: tags?.filter((t) => t.scopeReferenceId || t.scopeReferenceName),
      documentType,
      uploadEnd,
      uploadStart,
      saveToMainStore,
      saveToEnd,
      scrollRef,
      mainText,
      headerText,
      onSuccessAddingExisting,
      footerText,
      withoutAddExisting,
      addExistingText,
      headerDescription,
      addingNcFile,
      addExistingSearchLevel,
      noPhoto,
      onlySelectImage,
      onSuccessImageUpload,
      imageDocumentReferences,
      scale,
      font,
      expectedFileName,
    });
  };

  browse = ({
    ncFile,
    tags,
    documentType = null,
    uploadEnd,
    uploadStart,
    saveToMainStore = false,
    saveToEnd = false,
    scrollRef,
    onlySelectImage,
    withoutAddExisting,
    addExistingText,
    mainText,
    headerText,
    onSuccessAddingExisting,
    footerText,
    headerDescription,
    addingNcFile,
    addExistingSearchLevel,
    noPhoto,
  }) => {
    this.props?.setNcFileUploadDocumentPickerOpened(false);
    this.setState({
      shouldBrowse: true,
      showUploadImage: true,
      ncFile,
      tags: tags?.filter((t) => t.scopeReferenceId || t.scopeReferenceName),
      documentType,
      uploadEnd,
      uploadStart,
      saveToMainStore,
      saveToEnd,
      scrollRef,
      mainText,
      headerText,
      onSuccessAddingExisting,
      footerText,
      withoutAddExisting,
      addExistingText,
      headerDescription,
      addingNcFile,
      addExistingSearchLevel,
      noPhoto,
      onlySelectImage,
    });
  };

  hide = () => {
    this.setState({
      showUploadImage: false,
      ncFile: '',
      tags: [],
      documentType: null,
      uploadEnd: null,
      uploadStart: null,
      saveToMainStore: false,
      onlySelectImage: false,
      saveToEnd: false,
      scrollRef: null,
      mainText: null,
      headerText: null,
      onSuccessAddingExisting: null,
      footerText: null,
      withoutAddExisting: false,
      addExistingText: null,
      headerDescription: null,
      addingNcFile: false,
      addExistingSearchLevel: null,
      noPhoto: false,
      imageDocumentReferences: [],
      scale: undefined,
      font: undefined,
      expectedFileName: null,
    });
  };

  render() {
    if (!this.state.showUploadImage) {
      return null;
    }
    return <RenderComponent {...{ ...this.state, hide: this.hide }} />;
  }
}

export default connect(null, { setNcFileUploadDocumentPickerOpened }, null, { forwardRef: true })(ImageUpload);
